// Links from a data point to the record it came from. Only link formats
// checked in a browser are built (2026-10-12):
//
//   WattApp     https://www.wattapp.no/?showPlace=<station id>&location=<name>
//               What WattApp's own share button produces. The station id is
//               the feed's elbits:Substation.id (grid_point.station_id).
//   Se eiendom  https://seeiendom.kartverket.no/eiendom/<knr>/<gnr>/<bnr>/<fnr>/<snr>
//               The property page: boundary, area, buildings, heritage.
//   Norgeskart  https://norgeskart.no/#!?project=norgeskart&zoom=<z>&lat=<N>&lon=<E>&markerLat=<N>&markerLon=<E>
//               EPSG:25833 coordinates, the form Se eiendom links with.
//
// Publisher, licence and dataset pages come from the backend source registry
// (fieldProvenance.fetchSource); this file only knows how to point at one
// record.

// WGS84 → ETRS89 / UTM zone 33N (EPSG:25833), Krüger series (Karney 2011,
// three terms). Well under a metre against pyproj across Norway.
export function toUtm33(lat, lon) {
  const a = 6378137, f = 1 / 298.257222101, k0 = 0.9996;
  const n = f / (2 - f);
  const A = (a / (1 + n)) * (1 + (n * n) / 4 + n ** 4 / 64);
  const alpha = [n / 2 - (2 * n * n) / 3 + (5 * n ** 3) / 16, (13 * n * n) / 48 - (3 * n ** 3) / 5, (61 * n ** 3) / 240];
  const e = Math.sqrt(f * (2 - f));
  const phi = (lat * Math.PI) / 180;
  const lam = ((lon - 15) * Math.PI) / 180;
  const t = Math.sinh(Math.atanh(Math.sin(phi)) - e * Math.atanh(e * Math.sin(phi)));
  const xi0 = Math.atan2(t, Math.cos(lam));
  const eta0 = Math.atanh(Math.sin(lam) / Math.sqrt(1 + t * t));
  let xi = xi0, eta = eta0;
  alpha.forEach((al, i) => {
    const j = 2 * (i + 1);
    xi += al * Math.sin(j * xi0) * Math.cosh(j * eta0);
    eta += al * Math.cos(j * xi0) * Math.sinh(j * eta0);
  });
  return { e: 500000 + k0 * A * eta, n: k0 * A * xi };
}

const num = (v) => (v == null || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);

export function wattappStationUrl(gp) {
  if (!gp?.station_id) return null;
  const q = new URLSearchParams({ showPlace: gp.station_id });
  if (gp.name) q.set('location', gp.name);
  return `https://www.wattapp.no/?${q.toString()}`;
}

// knr from kommunenummer / kommunenr; gnr and bnr from explicit fields or
// the first "gnr/bnr" in matrikkelnummer, which can be a list ("20/23,128").
export function seEiendomUrl(site) {
  const pp = site?._parcel_props || {};
  const knrRaw = site?.kommunenummer ?? site?.kommunenr ?? pp.kommunenr ?? pp.kommunenummer;
  const knr = knrRaw != null && /^\d{3,4}$/.test(String(knrRaw).trim()) ? String(knrRaw).trim().padStart(4, '0') : null;
  if (!knr) return null;
  let gnr = num(site?.gnr ?? pp.gnr), bnr = num(site?.bnr ?? pp.bnr), fnr = 0, snr = 0;
  if (gnr == null || bnr == null) {
    const m = String(site?.matrikkelnummer ?? pp.matrikkelnummer ?? '').match(/(\d+)\s*\/\s*(\d+)(?:\s*\/\s*(\d+))?(?:\s*\/\s*(\d+))?/);
    if (!m) return null;
    [gnr, bnr, fnr, snr] = [Number(m[1]), Number(m[2]), Number(m[3] || 0), Number(m[4] || 0)];
  }
  return `https://seeiendom.kartverket.no/eiendom/${knr}/${gnr}/${bnr}/${fnr}/${snr}`;
}

export function norgeskartUrl(lat, lon, zoom = 15) {
  const la = num(lat), lo = num(lon);
  if (la == null || lo == null) return null;
  const { e, n } = toUtm33(la, lo);
  const N = Math.round(n), E = Math.round(e);
  return `https://norgeskart.no/#!?project=norgeskart&zoom=${zoom}&lat=${N}&lon=${E}&markerLat=${N}&markerLon=${E}`;
}

// The record behind a data point, by kind (fieldProvenance entries name the
// kind in `record`). Returns {url, label} or null when the site lacks the key.
const RECORDS = {
  station: (site) => {
    const gp = site?.harbour_screen?.grid_point;
    const url = wattappStationUrl(gp);
    return url && { url, label: `Open ${gp.name || 'the station'} in WattApp`, short: 'WattApp',
                    published: gp.updated_at || null, publisher: gp.owner || null };
  },
  parcel: (site) => {
    const url = seEiendomUrl(site);
    return url && { url, label: 'Open the property in Se eiendom', short: 'Se eiendom' };
  },
  terrain: (site) => {
    const url = norgeskartUrl(site?.lat, site?.lon);
    return url && { url, label: 'Show the terrain on Norgeskart', short: 'Norgeskart' };
  },
  // The plan that decides the zoning status, as DIBK serves it (the same
  // GetFeatureInfo as a web page). Falls back to any plan layer read.
  plan: (site) => {
    const pr = site?.plan_readings;
    if (!pr) return null;
    const by = { current: 'reguleringsplan', future_A: 'kommuneplan', not_allowed: 'reguleringsplan' }[site?.zoning_status];
    const order = [by, pr.rp_forslag?.code ? 'rp_forslag' : null, pr.kp_forslag?.code ? 'kp_forslag' : null,
                   'reguleringsplan', 'kommuneplan'].filter(Boolean);
    for (const k of order) {
      const r = pr[k];
      if (r?.link && (r.code || r.plan_name)) {
        return { url: r.link, label: `Open the ${r.layer || k} record at DIBK${r.plan_name ? ` (${r.plan_name})` : ''}`,
                 short: 'DIBK plan register', published: r.updated || r.in_force || null };
      }
    }
    return null;
  },
  co2: (site) => {
    const c = site?.nearest_co2_source;
    return c?.source_url ? { url: c.source_url, label: `Open ${c.name || 'the facility'} in Norskeutslipp`,
                             short: 'Norskeutslipp', published: c.year ? String(c.year) : null } : null;
  },
};

export function recordLink(kind, site) {
  const build = RECORDS[kind];
  return build ? build(site) || null : null;
}

// Every record a site links to, for a one-line "Sources" list.
export function siteRecordLinks(site) {
  return ['station', 'parcel', 'plan', 'terrain'].map(k => recordLink(k, site)).filter(Boolean);
}
